'use client';

import { useState } from 'react';

import { cn } from '@ui/lib/utils';

import { ImageWithFallback } from '../../../common/imageWithFallback/ImageWithFallback';
import TickCircle from '../../../icons/TickCircle';
import { StarRating } from '../starRating/StarRating';

export interface ReviewCardProps {
  userName: string;
  userAvatarUrl?: string;
  date: string;
  rating: number;
  comment: string;
  tags?: string[];
  className?: string;
  maxCommentLength?: number;
  maxVisibleTags?: number;
  timeAgo?: string;
  onClick?: () => void;
  /** When provided, "See more" opens a detail view instead of expanding inline. */
  onSeeMore?: () => void;
  seeMoreLabel?: string;
  seeLessLabel?: string;
  userLikedLabel?: string;
  /** Optional action element rendered in the header row (e.g. Archive/Post button). */
  headerAction?: React.ReactNode;
}

export const ReviewCard = ({
  userName,
  userAvatarUrl,
  date,
  rating,
  comment,
  tags,
  className,
  maxCommentLength = 150,
  maxVisibleTags = 3,
  onClick,
  onSeeMore,
  seeMoreLabel = 'See more',
  seeLessLabel = 'See less',
  userLikedLabel = 'User liked:',
  headerAction
}: ReviewCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const shouldTruncate = comment.length > maxCommentLength;
  const displayComment = shouldTruncate && !expanded ? comment.slice(0, maxCommentLength) + '...' : comment;

  const visibleTags = tags?.slice(0, maxVisibleTags);
  const hiddenTagCount = tags ? tags.length - maxVisibleTags : 0;

  return (
    <div
      className={cn('flex flex-col gap-3 rounded-lg min-w-0 overflow-hidden', onClick && 'cursor-pointer', className)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClick();
            }
          : undefined
      }
    >
      {/* Reviewer info: avatar + name + date + optional action */}
      <div className="flex gap-2 items-center">
        <div className="relative size-9 rounded-full overflow-hidden shrink-0 bg-neutral-100">
          {userAvatarUrl && (
            <ImageWithFallback src={userAvatarUrl} alt={userName} className="size-full object-cover" sizes="36px" />
          )}
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <p className="text-sm font-medium text-neutral-800 leading-normal truncate">{userName}</p>
          <p className="text-xs text-neutral-600 leading-normal truncate">{date}</p>
        </div>
        {headerAction && <div className="shrink-0 ml-auto">{headerAction}</div>}
      </div>

      <StarRating value={rating} size="sm" readonly />

      <div className="flex flex-col gap-1 items-start">
        <p className="text-sm text-neutral-800 leading-normal break-words">{displayComment}</p>
        {shouldTruncate && (
          <button
            type="button"
            className="cursor-pointer text-sm font-semibold text-[#1877F2] hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              if (onSeeMore) {
                onSeeMore();
              } else {
                setExpanded(!expanded);
              }
            }}
          >
            {onSeeMore ? seeMoreLabel : expanded ? seeLessLabel : seeMoreLabel}
          </button>
        )}
      </div>

      {visibleTags && visibleTags.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-neutral-600 leading-normal">{userLikedLabel}</p>
          <div className="flex flex-wrap gap-2">
            {visibleTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 bg-neutral-100 border border-neutral-200 rounded pl-1.5 pr-2 py-1 text-xs text-neutral-700"
              >
                <TickCircle className="size-3 shrink-0" />
                {tag}
              </span>
            ))}
            {hiddenTagCount > 0 && (
              <span className="inline-flex items-center bg-neutral-100 border border-neutral-200 rounded px-1.5 py-1 text-xs text-neutral-800">
                +{hiddenTagCount}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
